class BlacklandExplore extends HTMLElement {
  #abort;
  #dialog;
  #trigger;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#dialog = this.querySelector('[data-explore-dialog]');
    this.#trigger = this.querySelector('[data-explore-trigger]');
    if (!this.#dialog || !this.#trigger) return;

    this.#trigger.setAttribute('role', 'button');
    this.#trigger.addEventListener('click', this.#open, { signal });
    this.#trigger.addEventListener('keydown', this.#onKeydown, { signal });
    this.querySelector('[data-explore-close]')?.addEventListener('click', this.#close, { signal });
    this.#dialog.addEventListener('close', this.#onClosed, { signal });
    this.#dialog.addEventListener('cancel', this.#close, { signal });
    this.#dialog.addEventListener('keydown', this.#trapFocus, { signal });
    this.#dialog.addEventListener('click', this.#onNavigation, { capture: true, signal });
    document.addEventListener('shopify:section:unload', this.#onUnload, { signal });


  }

  disconnectedCallback() {
    this.#close();
    this.#abort?.abort();
  }

  #open = (event) => {
    if (!this.#dialog?.showModal) return;
    event.preventDefault();
    if (this.#dialog.open) return;
    this.#dialog.showModal();
    this.#trigger.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('blackland-explore-open');
  };

  #close = () => {
    if (this.#dialog?.open) this.#dialog.close();
    this.#onClosed();
  };

  #onClosed = () => {
    this.#trigger?.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('blackland-explore-open');
  };

  #onKeydown = (event) => {
    if (event.key === ' ') this.#open(event);
  };

  #trapFocus = (event) => {
    if (event.key !== 'Tab') return;
    const focusable = [];
    const collect = (root) => {
      for (const element of root.children) {
        if (element.matches('a[href], button, input, select, textarea, [tabindex]') && element.tabIndex >= 0 && !element.disabled && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden' && !element.closest('[inert]')) focusable.push(element);
        if (element.shadowRoot) collect(element.shadowRoot);
        else collect(element);
      }
    };
    collect(this.#dialog);
    let active = document.activeElement;
    while (active?.shadowRoot?.activeElement) active = active.shadowRoot.activeElement;
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  #onNavigation = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target === this.#dialog) {
      const bounds = this.#dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.#close();
      return;
    }
    if (target?.closest('a[href], search-button')) this.#close();
  };

  #onUnload = (event) => {
    if (event.target instanceof Element && event.target.contains(this)) this.#close();
  };
}

if (!customElements.get('blackland-explore')) customElements.define('blackland-explore', BlacklandExplore);
